<template>
    <v-container>
        <v-row>
            <v-col cols="12">
                <v-row >
                    <v-col cols="12" lg="5" offset-lg="2">
                        <v-text-field
                            label="Specimen ID"
                            v-model="enteredSpecimenID"
                            @keyup.native="checkEnterKey"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" lg="3">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-btn
                                    block
                                    color="primary" 
                                    :loading="loadingSpecimen" 
                                    @click="loadSpecimen"
                                >
                                    Go
                                </v-btn>
                            </v-col>
                            <v-col cols="12" sm="6" v-if="specimenLoaded">
                                <v-btn
                                    block
                                    color="info"
                                    :loading="loadingSpecimen"
                                    @click="printBarcode()"
                                >
                                    Print Barcode
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row v-if="specimenLoaded" >
                    <v-col cols="12">
                        <v-card>
                            <v-card-title class="text-center">
                                <div class="title">
                                    Specimen: {{currentSpecimen.SpecimenDisplayID}}
                                </div>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="success"
                                    @click="addMedicalRecord"
                                    v-if="auth.hasPermissions([permissions.ACCESS_LEVELS.VET_MED_TEAM], true)"
                                >
                                    Add New Medical Record
                                </v-btn>
                            </v-card-title>
                            <v-card-text>
                                <v-row >
                                    <v-col cols="12" sm="12" md="6" >
                                        <v-row  class="specimen-details">
                                            <v-col cols="4" sm="3" class="specimen-details-header">
                                                Number of Open Medical Records
                                            </v-col>
                                            <v-col cols="8" sm="9">
                                                {{ totalMedicalRecords}}
                                            </v-col>
                                        </v-row>
                                        <v-row  class="specimen-details">
                                            <v-col cols="4" sm="3" class="specimen-details-header">
                                                Gender
                                            </v-col>
                                            <v-col cols="8" sm="9">
                                                {{ genderFull}}
                                            </v-col>
                                        </v-row>
                                        <v-row  :class="`specimen-details ${lastCleanClass}`">
                                            <v-col cols="4" sm="3" class="specimen-details-header">
                                                Last Clean
                                            </v-col>
                                            <v-col cols="8" sm="9">
                                                {{ lastCleanDateString }}
                                            </v-col>
                                        </v-row>
                                        <v-row  class="specimen-details">
                                            <v-col cols="4" sm="3" class="specimen-details-header">
                                                Location
                                            </v-col>
                                            <v-col cols="8" sm="9">
                                                {{ locationFullText }}
                                            </v-col>
                                        </v-row>
                                        <v-row  class="specimen-details">
                                            <v-col cols="4" sm="3" class="specimen-details-header">
                                                Supplier
                                            </v-col>
                                            <v-col cols="8" sm="9">
                                                {{ currentSpecimen.SupplierName}}
                                            </v-col>
                                        </v-row>
                                        <v-row >
                                            <v-col cols="12">
                                                <v-alert
                                                    :value="annualCleanDue"
                                                    type="info"
                                                    icon="alarm"
                                                >
                                                    <span>Annual Clean Due</span>
                                                </v-alert>
                                            </v-col>
                                        </v-row>

                                        <v-row >
                                            <v-col cols="12" v-if="currentSpecimen.Deceased">
                                                <v-alert
                                                    :value="true"
                                                    type="error"
                                                    outlined
                                                >
                                                    <span>Specimen is deceased. No further daily husbandry records may be added. If this is incorrect, please contact a system administrator to mark the specimen as not deceased.</span>
                                                </v-alert>
                                            </v-col>
                                        </v-row>
                                        <v-row >
                                            <v-col cols="12" v-if="currentSpecimen.Euthanised">
                                                <v-alert
                                                    :value="true"
                                                    type="error"
                                                    outlined
                                                >
                                                    <span>Specimen is euthanised. If this is incorrect, please contact a system administrator to mark the specimen as not euthanised.</span>
                                                </v-alert>
                                            </v-col>
                                        </v-row>
                                        <v-row >
                                            <v-col cols="12" v-if="currentSpecimen.Aggressive">
                                                <v-alert
                                                    :value="true"
                                                    color="error"
                                                    type="error"
                                                    outlined
                                                >
                                                    <span>Specimen is flagged as aggressive.</span>
                                                </v-alert>
                                            </v-col>
                                        </v-row>
                                        <v-row >
                                            <v-col cols="12">
                                                <v-row >
                                                    <v-col cols="12" v-if="!previousCleanLoading && latestEventDetails.LatestControlledOccurrence && latestEventDetails.LatestControlledOccurrence.length > 0">
                                                        <v-alert
                                                            :value="true"
                                                            type="error"
                                                        >
                                                            <div>
                                                                <div v-for="item in latestEventDetails.LatestControlledOccurrence" :key="item.Identifer">
                                                                    <div class="previous-clean-title">
                                                                        Latest {{ item.Type + " - " }}
                                                                        <span v-if="item.Type === 'Daily Husbandry'">{{ item.EventDate | formatTasksDate }}</span>
                                                                        <span v-else>{{ item.EventDate | formatNonTasksDate }}</span>
                                                                    </div>
                                                                    <div class="display-new-line" >{{ item.Notes }}</div>
                                                                </div>
                                                            </div>
                                                            <v-progress-circular
                                                                indeterminate
                                                                color="primary"
                                                                v-if="previousCleanLoading"
                                                            ></v-progress-circular>
                                                        </v-alert>
                                                    </v-col>

                                                    <v-col cols="12" v-if="!previousCleanLoading && latestEventDetails.LatestDangerousOccurrence && latestEventDetails.LatestDangerousOccurrence.length > 0">
                                                        <v-alert
                                                            :value="true"
                                                            type="error"
                                                        >
                                                            <div>
                                                                <div v-for="item in latestEventDetails.LatestDangerousOccurrence" :key="item.Identifer">
                                                                    <div class="previous-clean-title">
                                                                        Latest {{ item.Type + " - " }}
                                                                        <span v-if="item.Type === 'Daily Husbandry'">{{ item.EventDate | formatTasksDate }}</span>
                                                                        <span v-else>{{ item.EventDate | formatNonTasksDate }}</span>
                                                                    </div>
                                                                    <div class="display-new-line" >{{ item.Notes }}</div>
                                                                </div>
                                                            </div>
                                                            <v-progress-circular
                                                                indeterminate
                                                                color="primary"
                                                                v-if="previousCleanLoading"
                                                            ></v-progress-circular>
                                                        </v-alert>
                                                    </v-col>

                                                    <v-col cols="12" v-if="!previousCleanLoading && latestEventDetails.LatestMedicalObservation && latestEventDetails.LatestMedicalObservation.length > 0">
                                                        <v-alert
                                                            :value="true"
                                                            type="error"
                                                        >
                                                            <div>
                                                                <div v-for="item in latestEventDetails.LatestMedicalObservation" :key="item.Identifer">
                                                                    <div class="previous-clean-title">
                                                                        Latest {{ item.Type + " - " }}
                                                                        <span v-if="item.Type === 'Daily Husbandry'">{{ item.EventDate | formatTasksDate }}</span>
                                                                        <span v-else>{{ item.EventDate | formatNonTasksDate }}</span>
                                                                    </div>
                                                                    <div class="display-new-line" >{{ item.Notes }}</div>
                                                                </div>
                                                            </div>
                                                            <v-progress-circular
                                                                indeterminate
                                                                color="primary"
                                                                v-if="previousCleanLoading"
                                                            ></v-progress-circular>
                                                        </v-alert>
                                                    </v-col>

                                                    <v-col cols="12" v-if="!previousCleanLoading && latestEventDetails.LatestDailyHusbandry && latestEventDetails.LatestDailyHusbandry.length > 0">
                                                        <v-alert
                                                            :value="true"
                                                            type="info"
                                                            v-if="!previousCleanError"
                                                        >
                                                            <div>
                                                                <div v-for="item in latestEventDetails.LatestDailyHusbandry" :key="item.Identifer">
                                                                    <div class="previous-clean-title">
                                                                        Latest {{ item.Type + " - " }}
                                                                        <span v-if="item.Type === 'Daily Husbandry'">{{ item.EventDate | formatTasksDate }}</span>
                                                                        <span v-else>{{ item.EventDate | formatNonTasksDate }}</span>
                                                                    </div>
                                                                    <div class="display-new-line" >{{ item.Notes }}</div>
                                                                </div>
                                                            </div>
                                                            <v-progress-circular
                                                                indeterminate
                                                                color="primary"
                                                                v-if="previousCleanLoading"
                                                            ></v-progress-circular>
                                                        </v-alert>

                                                        <v-alert
                                                            :value="true"
                                                            type="error"
                                                            v-if="previousCleanError"
                                                        >
                                                            {{previousCleanError}}
                                                        </v-alert>
                                                    </v-col>
                                                </v-row>
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                    <v-col cols="12" sm="12" md="6" >
                                        <DailyHusbandryWeightHistory :history="currentSpecimen.WeightHistory">
                                        </DailyHusbandryWeightHistory>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" v-if="canEdit">
                        <v-card class="pa-0">
                            <v-expansion-panels v-model="panel">
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <div class="title">Daily Husbandry & Specimen Observation</div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <DailyHusbandryLogging
                                            :specimenID="currentSpecimen.SpecimenID"
                                            @onSaved="refreshFromSave"
                                            :currentLocationTypeID="currentSpecimen.LocationTypeID"
                                            :currentRoomID="currentSpecimen.RoomID"
                                            :currentSuiteNumberID="currentSpecimen.SuiteNumberID"
                                            :currentDrawerID="currentSpecimen.DrawerID"
                                            :currentRackNumber="currentSpecimen.RackNumber"></DailyHusbandryLogging>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <div class="title">Medical Observation</div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <add-medical-observation :specimenID="currentSpecimen.SpecimenID" @onSaved="refreshFromSave"></add-medical-observation>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <div class="title">Controlled Occurrence</div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <add-controlled-occurrence :specimenID="currentSpecimen.SpecimenID" :specimen="currentSpecimen" @onSaved="refreshFromSave"></add-controlled-occurrence>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <div class="title">Dangerous Occurrence</div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <add-dangerous-occurrence :specimenID="currentSpecimen.SpecimenID" @onSaved="refreshFromSave"></add-dangerous-occurrence>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-card>
                    </v-col>
                    <v-col cols="12">
                        <v-card>
                            <v-card-title class="title">Specimen History</v-card-title>
                            <v-row >
                                <v-col cols="12" >
                                    <DailyHusbandryHistory ref="historyLog" :displaySpecimenID="false" :searchCriteria="{ specimenID: currentSpecimen.SpecimenID}" @onSaved="refreshFromSave"></DailyHusbandryHistory>
                                </v-col>
                            </v-row>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import moment from 'moment';
import validationRules from "../../helpers/validationrules";
import GlobalEventBus from '@/globalEventBus';
import DailyHusbandryLogging from "./DailyHusbandryLogging";
import AddMedicalObservation from "@/components/specimens/AddMedicalObservation";
import AddControlledOccurrence from "@/components/specimens/AddControlledOccurrence";
import AddDangerousOccurrence from "@/components/specimens/AddDangerousOccurrence";
import DailyHusbandryHistory from "./DailyHusbandryHistory";
import DailyHusbandryWeightHistory from "./DailyHusbandryWeightHistory";
import * as auth from '@/services/auth';
import permissions from 'comunet-btg-utah-repository-shared/permissions';
import constants from 'comunet-btg-utah-repository-shared/constants';
import MedicalRecordAPI from '../../services/medicalRecordAPI';
import { mapState } from 'vuex';

export default {
    components: {
        DailyHusbandryLogging,
        AddMedicalObservation,
        AddControlledOccurrence,
        AddDangerousOccurrence,
        DailyHusbandryHistory,
        DailyHusbandryWeightHistory
    },
    props: {
        specimenDisplayID: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            auth: auth,
            permissions: permissions,
            currentSpecimen: {},
            rules: validationRules.rules,
            enteredSpecimenID: "",
            loadingSpecimen: false,
            panel: null,
            totalMedicalRecords: 0,
        };
    },
    filters: {
        formatTasksDate: function(value) {
            return moment.utc(value).format(constants.DATE_FORMAT_NO_TIME);
        },
        formatNonTasksDate: function(value) {
            return moment(value).format(constants.DATE_FORMAT);
        }
    },
    computed: {
        ...mapState("dailyHusbandryStore", {
            latestEventDetails: state => state.latestEventDetails
        }),
        locationFullText() {
            return [this.currentSpecimen.LocationTypeText, this.currentSpecimen.RoomText, this.currentSpecimen.SuiteNumberText, this.currentSpecimen.RackNumber, this.currentSpecimen.DrawerText].filter(x => x).join(" - ");
        },
        specimenLoaded() {
            return !this.loadingSpecimen && this.currentSpecimen != null && this.currentSpecimen.SpecimenID && this.currentSpecimen.SpecimenID > 0;
        },
        genderFull() {
            if (!this.specimenLoaded){
                return "";
            }

            if (this.currentSpecimen.Gender === "M") {
                return "Male";
            }

            if (this.currentSpecimen.Gender === "F") {
                return "Female";
            }

            // gender can be null
            return "";
        },
        lastCleanDateString() {
            if (!this.specimenLoaded){
                return "";
            }

            if (!this.currentSpecimen.LastCleanDate){
                return "No clean recorded";
            } else {
                return moment.utc(this.currentSpecimen.LastCleanDate).format(constants.DATE_FORMAT_NO_TIME);
            }
        },
        lastCleanClass() {
            if (!this.specimenLoaded || !this.currentSpecimen.LastCleanDate){
                return "";
            }

            return moment.utc(this.currentSpecimen.LastCleanDate).add(30, 'd') > moment() ? "clean-date-valid" : "clean-date-invalid"
        },
        saveError() {
            return this.$store.state.specimenStore.saveError;
        },
        annualCleanDue() {
            return moment.utc(this.currentSpecimen.NextAnnualCleanDate) < moment();
        },
        previousCleanLoading() {
            return this.$store.state.dailyHusbandryStore.latestEventLoading
        },
        previousCleanError() {
            return this.$store.state.dailyHusbandryStore.latestEventLoadError
        },
        canEdit() {
            return auth.hasPermissions([permissions.ACCESS_LEVELS.DAILY_HUSBANDRY], true) && !this.currentSpecimen.Deceased;
        }
    },
    methods: {
        printBarcode() {
            let routeData = this.$router.resolve({name: 'printbarcode', params: { barcode: this.currentSpecimen.SpecimenDisplayID }});
            window.open(routeData.href, '_blank');
        },
        checkEnterKey(event){
            if (event.keyCode === 13){
                this.loadSpecimen();
            }
        },
        async loadSpecimen() {
            this.loadingSpecimen = true;
            this.currentSpecimen = {};

            let that = this;
            this.$store.dispatch("specimenStore/loadSpecimens", {
                params: {
                    SpecimenDisplayID: this.enteredSpecimenID
                },
                success: (data) => {
                    if (!(data.TotalSpecimens != 1)){
                        that.currentSpecimen = data.Specimens.map(function (l){
                            var species = that.$store.state.speciesStore.species.find(x => x.SpeciesID == l.SpeciesID);
                            l.Species = species;

                            var supplier = that.$store.state.supplierStore.suppliers.find(x => x.SupplierID == l.SupplierID);
                            l.Supplier = supplier;

                            return l;
                        })[0]

                        that.$router.replace('/husbandry/' + that.currentSpecimen.SpecimenDisplayID)
                        
                        that.$store.dispatch("dailyHusbandryStore/loadLatestEvent", { SpecimenID: that.currentSpecimen.SpecimenID })
                        
                        that.$refs.historyLog.refresh();

                        that.validSpecimen = true;
                    }
                },
                finally: () => {
                    that.loadingSpecimen = false;
                }
            });

            if(this.enteredSpecimenID) {
                const result = await MedicalRecordAPI.getMedicalRecords({SpecimenDisplayID: this.enteredSpecimenID, IncludeFollowup: "true", openOnly: 'true'});
                this.totalMedicalRecords = result.data.TotalMedicalRecords;
            }
        },
        processScan(scan) {
            if (scan.specimenDisplayID != ""){
                this.enteredSpecimenID = scan.specimenDisplayID;
                this.loadSpecimen();
            }
        },
        refreshFromSave() {
            // refreshes data in multiple components in this page

            // this fetches data for the latest events (the big blue box at the top of the page)
            this.$store.dispatch("dailyHusbandryStore/loadLatestEvent", { SpecimenID: this.currentSpecimen.SpecimenID });

            // this refetches the data for the Specimen History grid at the bottom of the page
            // using a method in the child component ensures that the correct pagination parameters are resent to the server
            this.$refs.historyLog.refresh();

            // also reload specimen data, so that the last clean date is updated
            this.loadSpecimen();

            // also hide all of the open expansion panels
            this.panel = null;
        },
        addMedicalRecord() {
            const routeData = this.$router.resolve({name: "medicalRecords", query: { SpecimenID: this.specimenDisplayID, OpenAdd: true}});
            window.open(routeData.href, '_blank');
        }
    },
    async created() {
        Promise.all([
            this.$store.dispatch("supplierStore/loadSuppliers"),
            this.$store.dispatch("speciesStore/loadSpecies")
        ]).then(() => {
            if (this.specimenDisplayID){
                // Load all the specimen data
                this.enteredSpecimenID = this.specimenDisplayID;
                this.loadSpecimen();
            }
        },
        () => {
            if (this.specimenDisplayID){
                // Load all the specimen data
                this.enteredSpecimenID = this.specimenDisplayID;
                this.loadSpecimen();
            }
        });
        GlobalEventBus.$on('onScan', this.processScan);

    },
    beforeDestroy(){
        GlobalEventBus.$off('onScan', this.processScan);
    }
};
</script>

<style scoped>
    .clean-date-valid {
        color: green;
    }
    .clean-date-invalid {
        color: red;
    }
    .display-new-line{
        white-space: pre-line;
    }

    .previous-clean-title {
        font-weight: bold;
        border-bottom: 1px solid;
    }

    .annual-clean-due-flex {
        padding-left: 10px;
    }

    .specimen-details .specimen-details-header {
        font-weight: bold;
    }

    .specimen-details .flex {
        min-height: 36px;
        font-size: 120%;
    }

    .location-buttons .first {
        padding-right: 5px;
    }

    .location-buttons .second {
        padding-left: 5px;
    }
</style>